package dev.akshaychordiya.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.akshaychordiya.MetaTextStyle
import dev.akshaychordiya.components.layouts.PageLayoutData
import dev.akshaychordiya.components.widgets.ListRowStyle
import dev.akshaychordiya.components.widgets.PageHeader
import dev.akshaychordiya.content.Mentoring
import dev.akshaychordiya.fadeUp
import dev.akshaychordiya.toSitePalette
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Div

@InitRoute
fun initNotFoundPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Page not found"))
}

/**
 * The 404.
 *
 * This exists for a less obvious reason than being friendly. Cloudflare Pages has no `404.html` to serve unless the
 * export produces one, and its fallback is to return `index.html` with **HTTP 200** - so every typo'd URL used to
 * render the homepage and report success. Search engines read that as a duplicate of `/`, which quietly undercuts the
 * canonical links pointing here from Medium. It also masked a real outage once: when the Pages Functions failed to
 * deploy, the api routes returned this same 200 homepage instead of an error.
 *
 * Kobweb has no `@Page` route that emits `404.html` - it handles unknown routes client-side via `setErrorPage`, which
 * never changes the HTTP status. So this is an ordinary page at `/not-found`, and `build.gradle.kts` exports a second
 * copy of it to `404.html` with `addExtraRoute`. That copy is what Cloudflare serves, with a real 404 status.
 *
 * Careful with wildcard paths in this comment: Kotlin nests block comments, so a literal slash-star inside a KDoc
 * opens a second comment level and silently swallows the rest of the file - including the annotation below.
 */
@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun NotFoundPage() {
    val palette = ColorMode.current.toSitePalette()

    PageHeader(
        eyebrow = "404",
        title = "This page doesn't exist",
        subtitle = "The link is probably old, or I moved something. Here's where everything actually lives.",
    )

    Column(Modifier.fillMaxWidth().gap(0.75.cssRem)) {
        destinations.forEachIndexed { index, destination ->
            Div(ListRowStyle.toModifier().fadeUp(index).toAttrs()) {
                Link(
                    path = destination.path,
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                ) {
                    Column(Modifier.fillMaxWidth().gap(0.3.cssRem)) {
                        SpanText(
                            destination.label,
                            Modifier.fontSize(1.1.cssRem).fontWeight(600).color(palette.brandPrimary),
                        )
                        SpanText(destination.blurb, MetaTextStyle.toModifier())
                    }
                }
            }
        }
    }

    SpanText(
        "If you followed a link from somewhere and it landed here, I'd genuinely like to know - it means something " +
            "of mine is pointing at the wrong place.",
        Modifier.margin(top = 2.cssRem).fontSize(0.95.cssRem).color(palette.subdued),
    )
}

private class Destination(val label: String, val blurb: String, val path: String)

private val destinations = listOf(
    Destination("Articles", "Everything I've written, oldest to newest.", "/articles"),
    Destination("Talks", "Conference talks, with the recordings where they exist.", "/talks"),
    Destination("Books", "Kotlin Blueprints, and what's next.", "/books"),
    Destination("Apps", "Indie apps on Google Play.", "/apps"),
    // Off-site, because booking lives on Topmate - see content/Mentoring.kt.
    Destination("1:1 sessions", "Career, Android, speaking, and moving abroad.", Mentoring.TOPMATE_PROFILE),
    Destination("About", "Who I am and what I work on.", "/about"),
)
